import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import chevronLeftSmall from "../../../../public/assets/images/chevron-left-small.svg"
import chevronRightSmall from "../../../../public/assets/images/chevron-right-small.svg"
import Datepicker from '../DatePicker/Datepicker'
import ValidationBanner from '../ValidationBanner'
import { AppDispatch, RootState } from '../../../store/store'
import {
  getCalendarView,
  clearCalendarView,
  mapCalendarViewResponse,
  CalendarViewFilters,
} from '../../../store/slices/Requestor/calendarViewSlice'
import Loader from '../Loader'
import { isValidDateFormat, isPastDate as isDateStringInPast } from '../../../utils/date'

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface EmployeeTimeOffRequest {
  rowId?: string
  requestId?: string 
  employeeName: string
  date: string
  status: string
  cellText?: string
}

interface CalendarProps {
  /**
   * Optional override for the request data. When omitted, the component
   * fetches its data from the calendar-view API based on the selected week.
   */
  requests?: EmployeeTimeOffRequest[]
  /** Optional filters forwarded to the calendar-view API. */
  filters?: CalendarViewFilters
  /** Optional start date from filter to set the datepicker value */
  filterStartDate?: string

  // Optional. Used by Approver flow.
  onWeekChange?: (weekStart:Date,selectedDate?: Date) => void
    isApproverFlow?: boolean
      // add
  selectedWeekStart?: Date
  onRequestClick?: (requestId: string) => void
  // Approver only
  approverStartDate?: string
  onApproverStartDateChange?: (date: string) => void
  /**
   * Callback invoked whenever the count of requests visible in the
   * currently displayed week changes (due to week navigation, filter
   * changes, or API response). Allows parent to update tab counts dynamically.
   */
  onWeeklyRequestCountChange?: (count: number) => void
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CURRENT_YEAR = new Date().getFullYear()
const MIN_YEAR = CURRENT_YEAR - 1
const MAX_YEAR = CURRENT_YEAR + 1
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000
const MIN_DATE = new Date(MIN_YEAR, 0, 1)
const MAX_DATE = new Date(MAX_YEAR, 11, 31)

const STATUS_COLOR_MAP: Record<string, string> = {
  approved: '#92CCF0',
  submitted: '#A4D751',
  resubmitted: '#A4D751',
  'on hold': '#FEC941',
  'on_hold': '#FEC941',
  onhold: '#FEC941',
  submitted_onhold: '#FEC941',
  returned: '#B2A8D2',
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function constrainYearToRange(year: number): number {
  return Math.min(MAX_YEAR, Math.max(MIN_YEAR, year))
}

function parseDateString(dateString: string): Date | null {
  // Parse YYYY-MM-DD as a LOCAL date to avoid timezone shifts (UTC parsing can
  // move dates to the previous day in local timezones and break week counts).
  const isoDateOnlyRegex = /^(\d{4})-(\d{2})-(\d{2})$/
  const isoDateOnlyMatch = isoDateOnlyRegex.exec(dateString)
  if (isoDateOnlyMatch) {
    const year = Number(isoDateOnlyMatch[1])
    const month = Number(isoDateOnlyMatch[2]) - 1
    const day = Number(isoDateOnlyMatch[3])
    const parsedDate = new Date(year, month, day)

    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate
    }
  }

  const isoDate = new Date(dateString)
  if (!Number.isNaN(isoDate.getTime())) {
    return isoDate
  }

  const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
  const match = dateRegex.exec(dateString)
  if (!match) {
    return null
  }

  const month = Number(match[1]) - 1
  const day = Number(match[2])
  const year = Number(match[3])
  const parsedDate = new Date(year, month, day)

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return parsedDate
}

function formatDateAsKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getWeekStartDate(date: Date): Date {
  const dayOfWeek = date.getDay()
  const weekStart = new Date(date)
  weekStart.setHours(0, 0, 0, 0)
  weekStart.setDate(weekStart.getDate() - dayOfWeek)
  return weekStart
}

function generateWeekDays(startDate: Date): Date[] {
  const weekStart = getWeekStartDate(startDate)
  const weekDays: Date[] = []

  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart.getTime() + i * MILLISECONDS_PER_DAY)
    day.setHours(0, 0, 0, 0)
    weekDays.push(day)
  }

  return weekDays
}

function formatOrdinalNumber(num: number): string {
  const lastTwoDigits = num % 100
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${num}th`
  }

  const lastDigit = num % 10
  switch (lastDigit) {
    case 1:
      return `${num}st`
    case 2:
      return `${num}nd`
    case 3:
      return `${num}rd`
    default:
      return `${num}th`
  }
}

function formatWeekRangeText(weekStart: Date, weekEnd: Date): string {
  const startMonthName = weekStart.toLocaleDateString('en-US', { month: 'long' })
  const endMonthName = weekEnd.toLocaleDateString('en-US', { month: 'long' })
  const startYear = weekStart.getFullYear()
  const endYear = weekEnd.getFullYear()

  if (startYear === endYear && startMonthName === endMonthName) {
    return `${startMonthName} ${weekStart.getDate()} - ${weekEnd.getDate()}, ${startYear}`
  }

  if (startYear === endYear) {
    return `${startMonthName} ${weekStart.getDate()} - ${endMonthName} ${weekEnd.getDate()}, ${startYear}`
  }

  return `${startMonthName} ${weekStart.getDate()}, ${startYear} - ${endMonthName} ${weekEnd.getDate()}, ${endYear}`
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CalendarWeekView: React.FC<CalendarProps> = ({ requests, filters, filterStartDate,  onWeekChange,  isApproverFlow = false,  selectedWeekStart,  onRequestClick,approverStartDate,onApproverStartDateChange, onWeeklyRequestCountChange }) => {
  const dispatch = useDispatch<AppDispatch>()
const requestorCalendarData = useSelector(
  (state: RootState) => state.calendarView.data
)

const requestorLoading = useSelector(
  (state: RootState) => state.calendarView.loading
)

const requestorError = useSelector(
  (state: RootState) => state.calendarView.error
)

const calendarData = isApproverFlow ? null : requestorCalendarData
// Covers the gap between mount and the first pending action so the loader is
// visible on the initial fetch too, not just on subsequent week changes.
const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false)
const isLoading = isApproverFlow ? false : (requestorLoading || !hasLoadedOnce)
const loadError = isApproverFlow ? null : requestorError
  const today = new Date()
  const [currentWeekStart, setCurrentWeekStart] = React.useState<Date>(() => {
      if (isApproverFlow && selectedWeekStart) {
    return getWeekStartDate(selectedWeekStart)
  }
    // Start with next week (7 days from today)
    const initialDate = new Date(today)
    initialDate.setDate(initialDate.getDate() + 7)
    initialDate.setFullYear(constrainYearToRange(initialDate.getFullYear()))
    return getWeekStartDate(initialDate)
  })

React.useEffect(() => {
  if (!selectedWeekStart) return

  const newWeek = getWeekStartDate(selectedWeekStart)

  if (formatDateAsKey(newWeek) !== formatDateAsKey(currentWeekStart)) {
    setCurrentWeekStart(newWeek)
  }
}, [selectedWeekStart, currentWeekStart])
  const [selectedDate, setSelectedDate] = React.useState<string>('')

  const getDateError = (): string => {
    if (!selectedDate) return ''
    if (!isValidDateFormat(selectedDate)) return 'Invalid date format'
    if (isDateStringInPast(selectedDate)) return 'This value is out of range'
    return ''
  }
  const dateError = getDateError()

 const weekDaysList = React.useMemo(() => generateWeekDays(currentWeekStart), [currentWeekStart])
  const weekStartDate = weekDaysList[0]
  const weekEndDate = weekDaysList[6]
  const canNavigateToPreviousWeek = weekStartDate > MIN_DATE
  const canNavigateToNextWeek = weekEndDate < MAX_DATE
  // Update selectedDate when filterStartDate changes (from filter apply)
  // Reset to next week when filterStartDate is removed (from filter reset/default)
  // Also watch filters - when filters become undefined, reset to next week
  React.useEffect(() => {
      if (isApproverFlow) return
    if (filterStartDate) {
      setSelectedDate(filterStartDate)
      // Also update the week view to show the filter start date's week
      const parsed = parseDateString(filterStartDate)
      if (parsed) {
        setCurrentWeekStart(getWeekStartDate(parsed))
      }
    } else if (filters && !filterStartDate && selectedDate) {
      // Filters applied without a filter date, but user has manually entered a date
      // → navigate the week view to show the selected date's week
      const parsed = parseDateString(selectedDate)
      if (parsed) {
        setCurrentWeekStart(getWeekStartDate(parsed))
      }
    } else if (filters === undefined || (filters && !filterStartDate && !selectedDate)) {
      // Reset to next week when:
      // 1. All filters are fully cleared (filters === undefined)
      // 2. Filters applied but no date selected and no manually entered date
      setSelectedDate('')
      const nextWeek = new Date()
      nextWeek.setDate(nextWeek.getDate() + 7)
      setCurrentWeekStart(getWeekStartDate(nextWeek))
    }
  }, [filterStartDate, filters,isApproverFlow])

  // Fetch calendar-view data whenever the displayed week (or filters) changes.
  // Skipped entirely when a `requests` override prop is provided.
  React.useEffect(() => {
     // Approver flow: parent handles API
  if (isApproverFlow) return
    // API contract: always send the selected week's START date in YYYY-MM-DD.
    const selectedDateISO = formatDateAsKey(getWeekStartDate(currentWeekStart))
    const promise = dispatch(
      getCalendarView({ selectedDate: selectedDateISO, ...(filters ? { filters } : {}) })
    )
    // Mark the initial fetch as settled regardless of outcome so the loader
    // hides once it completes (aborts count as settled too).
    promise.finally(() => setHasLoadedOnce(true))
    return () => {
      promise.abort()
    }
  }, [dispatch, currentWeekStart, filters, isApproverFlow])

  // Use the override prop when provided, otherwise the API-mapped data.
  const effectiveRequests = React.useMemo<EmployeeTimeOffRequest[]>(() => {
     if (isApproverFlow) {
    return requests ?? []
  }
    return mapCalendarViewResponse(calendarData)
  }, [requests, calendarData,isApproverFlow])

 

  // Check if a date is before today (past date)
  const isPastDate = (date: Date): boolean => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    return compareDate < today
  }

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    return compareDate.getTime() === today.getTime()
  }

  const requestsInCurrentWeek = React.useMemo(() => {
    return effectiveRequests
      .map((request) => {
        const parsedDate = parseDateString(request.date)
        if (!parsedDate) return null

        parsedDate.setHours(0, 0, 0, 0)
        return { ...request, parsedDate }
      })
      .filter((item): item is EmployeeTimeOffRequest & { parsedDate: Date } => {
        if (!item) return false
        return item.parsedDate >= weekStartDate && item.parsedDate <= weekEndDate
      })
  }, [effectiveRequests, weekStartDate, weekEndDate])

  const weeklyRequestCount = React.useMemo(() => {
    // Count unique requests for the week (not day cells). A single request can
    // span multiple days in calendar data, so rowId/requestId identity must be
    // deduplicated before updating tab count.
    const uniqueRequestKeys = new Set(
      requestsInCurrentWeek.map((request) => request.requestId || request.rowId)
    )
    return uniqueRequestKeys.size
  }, [requestsInCurrentWeek])

  /**
   * Notify parent of the updated request count for the currently displayed week.
   * This allows parent components to dynamically update tab counts based on
   * the visible requests in the week (filtered, not total).
   */
  React.useEffect(() => {
    onWeeklyRequestCountChange?.(weeklyRequestCount)
  }, [weeklyRequestCount, onWeeklyRequestCountChange])

  const calendarRows = React.useMemo(() => {
    const byRowId = new Map<string, { rowId: string; employeeName: string }>()

    requestsInCurrentWeek.forEach((request) => {
      const fallbackRowId = `${request.employeeName}-${request.status}-${request.date}`
      const rowId = request.rowId || fallbackRowId
      if (!byRowId.has(rowId)) {
        byRowId.set(rowId, { rowId, employeeName: request.employeeName })
      }
    })

    // Preserve first-seen insertion order from API-mapped rows.
    return Array.from(byRowId.values())
  }, [requestsInCurrentWeek])

  const requestsByRowAndDate = React.useMemo(() => {
    const lookup = new Map<string, Map<string, { status: string; cellText?: string,  requestId?: string }>>()

    requestsInCurrentWeek.forEach((request) => {
      const fallbackRowId = `${request.employeeName}-${request.status}-${request.date}`
      const rowId = request.rowId || fallbackRowId
      const dateKey = formatDateAsKey(request.parsedDate)
      const status = request.status
      const cellText = request.cellText

      if (!lookup.has(rowId)) {
        lookup.set(rowId, new Map())
      }
      lookup.get(rowId)?.set(dateKey, { status, cellText,  requestId: request.requestId, })
    })

    return lookup
  }, [requestsInCurrentWeek])

  const onDatepickerChange = (_name: string, value: string | string[]) => {
    const dateStr = Array.isArray(value) ? value[0] : value
    if (typeof dateStr !== 'string') return

    // Allow incomplete dates while typing (e.g., "15/05/202")
    // Also allow empty string to completely clear the field
if (isApproverFlow) {
  onApproverStartDateChange?.(dateStr)
} else {
  setSelectedDate(dateStr)
}
  }

const handleSearchClick = () => {
  const activeDate = isApproverFlow
    ? approverStartDate
    : selectedDate

  // If no date selected, reset to default initial week (next week)
  if (!activeDate || activeDate.trim() === '') {
    const nextWeek = new Date()
    nextWeek.setDate(nextWeek.getDate() + 7)

    const newWeekStart = getWeekStartDate(nextWeek)

    setCurrentWeekStart(newWeekStart)

    // Approver flow
    onWeekChange?.(newWeekStart)

    return
  }

  // Invalid format
  if (!isValidDateFormat(activeDate)) {
    if (!requests) {
      dispatch(clearCalendarView())
    }

    document
      .querySelector('[name="calendarDatePicker"]')
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })

    return
  }

  const parsed = parseDateString(activeDate)
  if (!parsed) return

  parsed.setHours(0, 0, 0, 0)

  let constrained = parsed

  if (constrained < MIN_DATE) {
    constrained = new Date(MIN_DATE)
  }

  if (constrained > MAX_DATE) {
    constrained = new Date(MAX_DATE)
  }

  const selectedDateObj = new Date(constrained)
  const newWeekStart = getWeekStartDate(selectedDateObj)

  setCurrentWeekStart(newWeekStart)

// Pass both the week start and the actual selected date
onWeekChange?.(newWeekStart, selectedDateObj)

  if (isDateStringInPast(activeDate)) {
    if (!requests) {
      dispatch(clearCalendarView())
    }
  }
}
const goToWeek = (weekOffset: number) => {
  const previousWeekStart = currentWeekStart

  const nextWeekStart = new Date(previousWeekStart)
  nextWeekStart.setDate(previousWeekStart.getDate() + weekOffset * 7)

  let finalWeekStart = getWeekStartDate(nextWeekStart)

  if (finalWeekStart < getWeekStartDate(MIN_DATE)) {
    finalWeekStart = getWeekStartDate(MIN_DATE)
  }

  const maxWeekStart = getWeekStartDate(MAX_DATE)

  if (finalWeekStart > maxWeekStart) {
    finalWeekStart = maxWeekStart
  }

  setCurrentWeekStart(finalWeekStart)

  // Approver flow only
  if (onWeekChange) {
    onWeekChange(finalWeekStart)
  }
}

  return (
    
    <div className="mt-6 calendar-compact-date">
<style>
{`
/* Mobile: Ensure date picker matches button width */
@media screen and (max-width: 767px) {
  .calendar-compact-date .date-field-wrapper,
  .calendar-compact-date .date-input-field {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }
}
`}
</style>
          {!requests && !isLoading && loadError && (
        <div className="mb-6">
          <ValidationBanner
            variant="error"
            heading="Error"
            message={loadError}
          />
        </div>
      )}
      <div className="mb-4 bg-[#E9EBEE] p-3 md:p-8! md:w-full!">
        <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-start xl-desktop:justify-between md:gap-6 md:bg-grey-100 rounded-md md:rounded-none p-3 md:p-0">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-start md:justify-start gap-3 md:gap-3 w-full md:w-auto !pt-4 md:p-2">
            <div className="w-full md:w-auto shrink-0 flex flex-col relative">
              <div className="flex items-center">
                <label htmlFor="date-input-calendarDatePicker" className="sr-only">Start Date</label>
                <Datepicker
                  openBehaviour="iconView"
                   value={
                   isApproverFlow
                   ? approverStartDate ?? selectedDate
                   : selectedDate
                   }
                  onChange={onDatepickerChange}
                  helperText='MM/DD/YYYY'
                  name="calendarDatePicker"
                  label='Start date'
                  customInputStyle={{ backgroundColor: '#ffffff', borderColor: '#0078B3', borderWidth: '2px' }}
                />
              </div>
              {dateError && (
                <p className="text-red-600 text-sm absolute top-[calc(100%-6px)] left-0 whitespace-nowrap">{dateError}</p>
              )}
            </div>

            <button
              type="button"
              className="w-full md:!w-[115px] px-4 py-2 button -inverted shrink-0 text-base md:text-sm whitespace-nowrap flex items-center justify-center h-10"
              aria-label="Search"
              onClick={handleSearchClick}
            >
              Search
            </button>
          </div>

          {/* Week Navigation */}
          <div className="w-full flex justify-center md:w-auto xl-desktop:absolute xl-desktop:left-1/2 xl-desktop:transform xl-desktop:-translate-x-1/2 items-center gap-1 shrink-0 px-3 py-3 md:py-2 rounded-md mb-4 md:mt-0 md:mb-0">
          <button
            type="button"
            className="p-1 rounded border border-transparent enabled:hover:border-[#D9E2EC] disabled:opacity-40 transition-colors"
            onClick={() => goToWeek(-1)}
            disabled={!canNavigateToPreviousWeek}
            aria-label="Previous week"
          >
            <img src={chevronLeftSmall} alt="Previous" className="w-5 h-5" />
          </button>

          <span className="p3 -medium whitespace-nowrap text-center font-medium md:text-base" style={{ fontSize: '18px' }}>
            {formatWeekRangeText(weekStartDate, weekEndDate)}
          </span>

        <button
          type="button"
          className="p-2 rounded border border-transparent enabled:hover:border-[#D9E2EC] disabled:opacity-40 transition-colors"
          onClick={() => goToWeek(1)}
          disabled={!canNavigateToNextWeek}
          aria-label="Next week"
        >
          <img src={chevronRightSmall} alt="Next" className="w-5 h-5" />
        </button>
        </div>
        </div>
      </div>

  

      {!requests && isLoading && (
        <Loader/>
      )}

      {(!isLoading || requests) && (
      <div 
        className={calendarRows.length > 13 ? 'overflow-y-scroll overflow-x-auto always-visible-scrollbar' : 'overflow-x-auto'} 
        style={calendarRows.length > 13 ? { maxHeight: '531px', maxWidth: '100%', WebkitOverflowScrolling: 'touch' } : { maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}
      >
        <table className="hidden md:table w-full border-collapse border border-[#D9E2EC] bg-white min-w-[800px]">
          <colgroup>
          <col style={{width:"auto", minWidth:"180px", maxWidth:"280px"}} />
          {weekDaysList.map((d) => (
            <col key={formatDateAsKey(d)} style={{width:"auto"}}/>
          ))}
          </colgroup>
          <thead className='sticky top-0 z-20'>
            <tr>
              <th className="sticky top-0 z-20 border border-[#D9E2EC] text-left min-w-[180px] max-w-[280px] bg-[#003B71] pl-4! pr-3 py-2 text-white">
                Name
              </th>
              {weekDaysList.map((day) => {
                const isTodayDate = isToday(day)
              return (
                <th
                  key={formatDateAsKey(day)}
                  className={`sticky top-0 z-20  text-center px-1 py-3! min-w-[100px] border border-[#D9E2EC] text-white ${
                    isTodayDate ? 'bg-[#0074AD]' : 'bg-[#003B71]'
                  }`}
                >
                  {day.toLocaleDateString('en-US', { weekday: 'long' })} {formatOrdinalNumber(day.getDate())}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {calendarRows.length === 0 && (
            <tr className="p-8!">
              <td colSpan={8} className="p-8! border border-[#D9E2EC] text-center text-[#52606D]">
                No time off requests found for this week.
              </td>
            </tr>
          )}

          {calendarRows.map((row) => (
            <tr className="text-left" key={row.rowId}>
              <td className="p4 -medium border border-[#D9E2EC] text-[#0074AD] pl-4! px-3 py-2! whitespace-nowrap overflow-hidden text-ellipsis max-w-[280px]">{row.employeeName}</td>

              {weekDaysList.map((day) => {
                const dayKey = formatDateAsKey(day)
                const isPast = isPastDate(day)
                const requestCell = requestsByRowAndDate.get(row.rowId)?.get(dayKey)
                const requestStatus = requestCell?.status
                const cellText = requestCell?.cellText
                const requestId= requestCell?.requestId
                
                // Determine background color: status color if present, otherwise gray for past dates
                let cellBackgroundColor: string | undefined
                if (requestStatus) {
                  const statusColor = STATUS_COLOR_MAP[requestStatus.toLowerCase()]
                  // Use status color if mapped, otherwise gray for past dates (even with unmapped status)
                  cellBackgroundColor = statusColor || (isPast ? '#E4E7EB' : undefined)
                } else if (isPast) {
                  cellBackgroundColor = '#E4E7EB' // Gray background for empty past dates
                }

                return (
                  <td
                    key={`${row.rowId}-${dayKey}`}
                    className={`align-middle text-center border border-[#D9E2EC] px-1.5 py-[3px] ${
                      isApproverFlow && requestId ? 'cursor-pointer' : ''
                    }`}
                    title={isApproverFlow ? `${row.employeeName} - ${day.toLocaleDateString('en-US')}` : undefined}
                    style={
                      cellBackgroundColor
                        ? { backgroundColor: cellBackgroundColor }
                        : undefined
                    }
                    onClick={() => {
                      if (isApproverFlow && requestId) {
                        onRequestClick?.(requestId)
                      }
                    }}
                  >
                    {requestStatus ? (
                      <span className="p3 -medium" title={isApproverFlow ? `${row.employeeName} - ${day.toLocaleDateString('en-US')}` : undefined}>{cellText || ''}</span>
                    ) : null}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      )}

      {/* Mobile View */}
      {(!isLoading || requests) && (
      <div className="md:hidden space-y-4 w-full min-w-[320px]">
        {calendarRows.length === 0 && (
          <div className="w-full px-4 py-2 border border-[#D9E2EC] bg-white text-center text-[#52606D]">
            No time off requests found for this week.
          </div>
        )}

        {weekDaysList.map((day) => {
          const dayKey = formatDateAsKey(day)
          const dayName = day.toLocaleDateString('en-US', { weekday: 'long' })
          const dayNumber = formatOrdinalNumber(day.getDate())
          const isPast = isPastDate(day)
          const isTodayDate = isToday(day)

          return (
            <div key={dayKey} className="w-full bg-white border-2 border-[#D9E2EC] min-w-[320px]">
              {/* Day Header - Full Width (no gray for past dates) */}
              <div className={`w-full px-4 py-3 font-medium text-base md:text-lg text-center border-b-2 border-[#D9E2EC] text-white ${
                isTodayDate ? 'bg-[#0074AD]' : 'bg-[#003B71]'
              }`}>
                {dayName} {dayNumber}
              </div>

              {/* Employee Rows */}
              <div className="divide-y-2 divide-[#D9E2EC]">
                {calendarRows.map((row) => {
                  const requestCell = requestsByRowAndDate.get(row.rowId)?.get(dayKey)
                  const requestStatus = requestCell?.status
                  const cellText = requestCell?.cellText
                  const requestId = requestCell?.requestId
                  
                  // Determine background color for mobile: status color if present, gray for past dates
                  let cellBackgroundColor: string | undefined
                  if (requestStatus) {
                    const statusColor = STATUS_COLOR_MAP[requestStatus.toLowerCase()]
                    // Use status color if mapped, otherwise gray for past dates
                    cellBackgroundColor = statusColor || (isPast ? '#E4E7EB' : undefined)
                  }

                  // Row background: gray only for past dates without status (or with unmapped status)
                  const rowBgClass = isPast && !cellBackgroundColor ? 'bg-[#E4E7EB]' : ''

                  return (
                    <div
                      key={`${row.rowId}-${dayKey}`}
                        className={`w-full px-3 py-3 flex items-center justify-between gap-3 min-h-12 ${rowBgClass}`}
                        title={isApproverFlow ? `${row.employeeName} - ${day.toLocaleDateString('en-US')}` : undefined}
                    >
                      <span className="font-medium text-sm px-2 py-2 text-[#0074AD] flex-1 min-w-0">
                        {row.employeeName}
                      </span>
                      {requestStatus && (
                        <span
                          className={`text-xs md:text-sm font-medium px-4 py-2 md:px-6 md:py-3 rounded text-center flex-none w-[30%] md:w-[140px] h-full ${
                            isApproverFlow && requestId ? 'cursor-pointer' : ''
                          }`}
                          title={isApproverFlow ? `${row.employeeName} - ${day.toLocaleDateString('en-US')}` : undefined}
                          style={{
                            backgroundColor: cellBackgroundColor || undefined,
                            width: "50%",
                            padding:" 16px 0 18px 0",
                          }}
                          onClick={() => {
                            if (isApproverFlow && requestId) {
                              onRequestClick?.(requestId)
                            }
                          }}
                        >
                          {cellText?.trim() ? cellText : requestStatus}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      )}
    </div>
  )
}

export default CalendarWeekView